import {readonly, ref} from 'vue'
import {invoke} from '@tauri-apps/api/core'
import {getVersion} from "@tauri-apps/api/app"

/**
 * POS Database Layer - SQLite abstraction for offline-first POS operations
 *
 * ORDER DATA STRUCTURE (BACKEND FORMAT):
 * =====================================
 * The entire codebase uses backend database format for consistency:
 *
 * Database Column: order_details (JSON TEXT)
 * JavaScript Property: orderDetails (array)
 *
 * OrderDetail Structure:
 * - menuItemId: number
 * - menuItemName: string (NOT "name" or "label")
 * - quantity: number
 * - unitPrice: number (NOT "price")
 * - itemSubtotal: number (calculated: (unitPrice + modifier total) * quantity)
 * - notes: string | null
 * - orderDetailModifiers: array of OrderDetailModifier objects
 *
 * OrderDetailModifier Structure:
 * - modifierGroupItemId: number
 * - modifierGroupName: string
 * - modifierOptionName: string
 * - appliedModType: 'Add' | 'Remove'
 * - quantity: number
 * - priceAdjustment: number
 *
 * Why backend format?
 * - Single source of truth - matches backend database exactly
 * - No transformation needed when syncing to server
 * - Easier to maintain - one format throughout
 * - No backward compatibility with old 'items' format
 */

// We only import sql.js when running in web mode (not Tauri), keeping the bundle size smaller for desktop builds
let SQL = null

// --- Reactive State ---
// This state is shared across all components using this composable.
// Think of it as a singleton database connection that everyone uses.
const dbInstance = ref(null) // In Tauri: stores the DB path string (e.g., 'sqlite:app.db'). In web: stores the actual sql.js Database object
const isDbReady = ref(false)
const isTauriMode = ref(false)
const isLoadingData = ref(false) // For long operations like sync

export function usePosDatabase() {

    // --- Core Initialization ---
    // This is the first function to call when the app starts. It:
    // 1. Detects if we're running in Tauri (desktop) or web browser
    // 2. Sets up the appropriate database (SQLite file for Tauri, in-memory for web)
    // 3. Creates our schema (tables) if they don't exist
    // 4. Supports device-specific databases via DEVICE_ID env var (e.g., app.db, appkds.db, apptablet.db)
    async function initializeDatabase() {
        console.log("Initializing POS database...")
        if (isDbReady.value) return // Already initialized, don't do it again

        try {
            await getVersion()
            isTauriMode.value = true
        } catch {
            isTauriMode.value = false
        }

        if (isTauriMode.value) {
            console.log("Tauri v2 environment: Initializing SQLite plugin via invoke.")

            // Check device type (for running multiple instances on same machine)
            // This allows us to test POS + KDS + Tablet simultaneously with separate databases
            // Set via: pnpm run tauri:dev:kds (creates appkds.db) or tauri:dev:tablet (creates apptablet.db)
            let deviceType = ''
            try {
                deviceType = await invoke('get_device_type') // Calls our Rust command to read env var
                console.log('Device type:', deviceType)
            } catch (err) {
                console.log('No device type set, using default database')
            }

            const DB_NAME = deviceType && deviceType !== 'pos' ? `app${deviceType}.db` : 'app.db'
            const dbPath = `sqlite:${DB_NAME}` // Format required by tauri-plugin-sql

            console.log('Database path:', dbPath)
            await invoke('plugin:sql|load', {db: dbPath})
            dbInstance.value = dbPath
        } else {
            console.log("Web environment: Initializing sql.js (in-memory).")
            if (!SQL) {
                const initSqlJs = (await import('sql.js')).default

                SQL = await initSqlJs({locateFile: file => `https://sql.js.org/dist/${file}`})
            }
            dbInstance.value = new SQL.Database()
        }

        // Create our tables (skip the isDbReady guard since we're initializing)
        await createTables()

        // Boot-time PRAGMAs. Default SQLite settings are pessimistic for a
        // long-running POS:
        //   * journal_mode=WAL: lets readers (KDS, dashboard) and writers
        //     (POS) work concurrently without blocking. Big perf win and
        //     critical for our offline-first concurrent-window pattern.
        //   * synchronous=NORMAL: WAL-friendly durability — survives app
        //     crash, may lose last fsync on full power-loss (acceptable
        //     for a POS that re-syncs on boot).
        //   * foreign_keys=ON: SQLite ships them OFF per-connection.
        //     Required for any FK constraints to actually enforce.
        //   * busy_timeout=5000ms: prevents SQLITE_BUSY when a write
        //     coincides with a sustained read; most consumer waits are
        //     well under this. Avoids surfacing transient errors to UI.
        // Skip on the sql.js (web) path — different engine, no WAL.
        if (isTauriMode.value) {
            try {
                await executeQuery('PRAGMA journal_mode = WAL', [], true)
                await executeQuery('PRAGMA synchronous = NORMAL', [], true)
                await executeQuery('PRAGMA foreign_keys = ON', [], true)
                await executeQuery('PRAGMA busy_timeout = 5000', [], true)
            } catch (pragmaErr) {
                console.warn('[POS DB] Failed to apply PRAGMAs (non-fatal):', pragmaErr)
            }
        }

        isDbReady.value = true // Now other functions can safely use the DB

        // Proactive migration sweep — re-seal any legacy plaintext rows for
        // sealed keys (terminalConfig, tyro_paired_*). Runs once per startup;
        // a no-op on already-sealed installs because looksLikeSealedCiphertext
        // short-circuits. Errors are non-fatal — a stuck-plaintext row gets
        // logged and the app keeps running.
        try {
            await migrateLegacyPlaintextRows()
        } catch (sweepErr) {
            console.warn('[POS DB] sealed-row migration sweep failed:', sweepErr)
        }

        // Auto-configure device mode based on device type (for KDS, Tablet, Kiosk builds)
        if (isTauriMode.value) {
            let deviceType = ''
            try {
                deviceType = await invoke('get_device_type')
                console.log('Setting device mode for:', deviceType)

                if (deviceType === 'kds') {
                    await savePosSetting('isKdsMode', '1')
                    console.log('KDS mode enabled')
                } else if (deviceType === 'tablet') {
                    await savePosSetting('isTabletMode', '1')
                    console.log('Tablet mode enabled')
                } else if (deviceType === 'kiosk') {
                    await savePosSetting('isKioskMode', '1')
                    console.log('Kiosk mode enabled')
                }
            } catch (err) {
                console.log('No device type detected, running as main POS')
            }
        }

        console.log("POS Database is ready.")
    }

    // --- Abstraction Layer ---
    // These internal functions handle the differences between Tauri and web environments.
    // All our public functions use these, so we only need to handle platform differences in one place.
    //
    // Why this matters: Tauri uses invoke() to call Rust commands, while web uses sql.js directly.
    // Instead of checking isTauriMode everywhere, we abstract it here.

    // Executes queries that modify data (INSERT, UPDATE, DELETE, CREATE)
    // Returns: { rowsAffected: number }
    async function executeQuery(sql, bindings = [], skipGuard = false) {
        if (!skipGuard && !isDbReady.value) throw new Error('DB not ready for execute.')

        if (isTauriMode.value) {
            return invoke('plugin:sql|execute', {
                db: dbInstance.value,
                query: sql,
                values: bindings,
            })
        } else {
            dbInstance.value.run(sql, bindings)

            return {rowsAffected: dbInstance.value.getRowsModified()}
        }
    }

    // Executes SELECT queries and returns results as an array of objects
    // Each row becomes an object with column names as keys
    async function selectQuery(sql, bindings = [], skipGuard = false) {
        if (!skipGuard && !isDbReady.value) throw new Error('DB not ready for select.')

        if (isTauriMode.value) {
            return invoke('plugin:sql|select', {
                db: dbInstance.value,
                query: sql,
                values: bindings,
            })
        } else {
            const results = []
            const stmt = dbInstance.value.prepare(sql)

            stmt.bind(bindings)
            while (stmt.step()) {
                results.push(stmt.getAsObject())
            }
            stmt.free()

            return results
        }
    }

    // --- Table Management ---
    // Creates our database schema. Uses IF NOT EXISTS so it's safe to call multiple times.
    // Schema includes: config (settings), configured_printers, pos_cache (synced business data),
    // users (staff), and local_orders (the heart of our offline-first POS)
    //
    // IMPORTANT: Order data structure uses BACKEND FORMAT throughout:
    // - Column: order_details (NOT items)
    // - Property: orderDetails (array of OrderDetail objects with menuItemName, unitPrice, itemSubtotal, orderDetailModifiers)
    // - No backward compatibility with old 'items' format
    async function createTables() {
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS config
                 (
                     key TEXT PRIMARY KEY NOT NULL,
                     value TEXT NOT NULL
                 );`,
                [],
                true,
        )
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS configured_printers
                 (
                     id            INTEGER PRIMARY KEY AUTOINCREMENT,
                     friendly_name TEXT NOT NULL,
                     system_name   TEXT NOT NULL,
                     destination   TEXT NOT NULL,
                     is_enabled    BOOLEAN DEFAULT TRUE
                 );`,
                [],
                true,
        )
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS scale_config
                 (
                     id          INTEGER PRIMARY KEY CHECK (id = 1),
                     enabled     BOOLEAN DEFAULT FALSE,
                     device_name TEXT,
                     port        TEXT,
                     baud_rate   INTEGER DEFAULT 9600,
                     protocol    TEXT    DEFAULT 'Standard',
                     unit        TEXT    DEFAULT 'kg'
                 );`,
                [],
                true,
        )
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS pos_cache
                 (
                     key TEXT PRIMARY KEY NOT NULL,
                     value     TEXT NOT NULL,
                     synced_at TEXT NOT NULL
                 );`,
                [],
                true,
        )
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS users
                 (
                     id   INTEGER PRIMARY KEY AUTOINCREMENT,
                     name TEXT NOT NULL,
                     pin  TEXT
                 );`,
                [],
                true,
        )
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS local_orders
                 (
                     id                     TEXT PRIMARY KEY NOT NULL,
                     order_number           INTEGER          NOT NULL,
                     order_type             TEXT             NOT NULL,
                     table_id               INTEGER,
                     table_name             TEXT,
                     floor_plan_id          INTEGER,
                     customer_type          TEXT,
                     customer_name          TEXT,
                     customer_phone         TEXT,
                     customer_email         TEXT,
                     delivery_address_line1 TEXT,
                     delivery_city          TEXT,
                     delivery_state         TEXT,
                     delivery_zip_code      TEXT,
                     delivery_country       TEXT,
                     delivery_latitude      REAL,
                     delivery_longitude     REAL,
                     delivery_distance_km   REAL,
                     delivery_fee           REAL    DEFAULT 0,
                     order_details          TEXT             NOT NULL, -- JSON array of OrderDetail objects (backend format: menuItemName, unitPrice, itemSubtotal, orderDetailModifiers)
                     applied_deals          TEXT,                      -- JSON array of applied deals
                     discount               REAL    DEFAULT 0,
                     discount_type          TEXT,
                     discount_value         REAL    DEFAULT 0,
                     tip                    REAL    DEFAULT 0,
                     tip_percent            REAL    DEFAULT 0,
                     notes                  TEXT,
                     subtotal               REAL             NOT NULL,
                     total                  REAL             NOT NULL,
                     status                 TEXT             NOT NULL,
                     priority               TEXT    DEFAULT 'normal',
                     is_online_order        INTEGER DEFAULT 0,
                     created_at             TEXT             NOT NULL,
                     updated_at             TEXT             NOT NULL,
                     synced_at              TEXT
                 );`,
                [],
                true,
        )
        try {
            await executeQuery(`ALTER TABLE local_orders
                ADD COLUMN priority TEXT DEFAULT 'normal'`, [], true)
        } catch (e) { /* column may already exist */
        }
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS message_tracking
                 (
                     uuid             TEXT PRIMARY KEY NOT NULL,
                     timestamp        INTEGER          NOT NULL,
                     event_name       TEXT             NOT NULL,
                     payload          TEXT             NOT NULL,
                     target_devices   TEXT             NOT NULL,
                     acknowledgements TEXT             NOT NULL,
                     retry_count      INTEGER DEFAULT 0,
                     status           TEXT    DEFAULT 'pending',
                     created_at       TEXT             NOT NULL
                 );`,
                [],
                true,
        )
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS processed_messages
                 (
                     uuid         TEXT PRIMARY KEY NOT NULL,
                     processed_at TEXT             NOT NULL,
                     event_name   TEXT             NOT NULL,
                     device_id    TEXT             NOT NULL
                 );`,
                [],
                true,
        )
        await executeQuery(
                `CREATE TABLE IF NOT EXISTS failed_messages
                 (
                     id                  INTEGER PRIMARY KEY AUTOINCREMENT,
                     uuid                TEXT    NOT NULL,
                     event_name          TEXT    NOT NULL,
                     payload             TEXT    NOT NULL,
                     target_devices      TEXT    NOT NULL,
                     acknowledgements    TEXT    NOT NULL,
                     retry_count         INTEGER NOT NULL,
                     first_attempt_at    TEXT    NOT NULL,
                     failed_at           TEXT    NOT NULL,
                     failure_reason      TEXT,
                     manually_retried    INTEGER DEFAULT 0,
                     manually_retried_at TEXT
                 );`,
                [],
                true,
        )

        await executeQuery(
                `CREATE TABLE IF NOT EXISTS order_save_audit_log
                 (
                     id                INTEGER PRIMARY KEY AUTOINCREMENT,
                     backend_order_id  INTEGER,
                     local_order_id    TEXT,
                     event_source      TEXT NOT NULL,
                     order_details     TEXT NOT NULL,
                     first_item_status TEXT,
                     items_count       INTEGER,
                     timestamp         TEXT NOT NULL
                 );`,
                [],
                true,
        )

        await executeQuery(
                `CREATE TABLE IF NOT EXISTS app_logs
                 (
                     id        INTEGER PRIMARY KEY AUTOINCREMENT,
                     timestamp TEXT    NOT NULL,
                     level     TEXT    NOT NULL DEFAULT 'INFO',
                     component TEXT    NOT NULL DEFAULT 'unknown',
                     message   TEXT    NOT NULL,
                     metadata  TEXT
                 );`,
                [],
                true,
        )

        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_app_logs_timestamp ON app_logs (timestamp);`,
                [],
                true,
        )

        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_app_logs_level ON app_logs (level);`,
                [],
                true,
        )

        await executeQuery(
                `CREATE TABLE IF NOT EXISTS terminal_transaction_logs
                 (
                     id             INTEGER PRIMARY KEY AUTOINCREMENT,
                     provider       TEXT NOT NULL,
                     txn_type       TEXT,
                     txn_ref        TEXT,
                     station_id     TEXT,
                     status         TEXT,
                     response_code  TEXT,
                     response_text  TEXT,
                     amount         REAL,
                     duration_ms    INTEGER,
                     xml_request    TEXT,
                     xml_response   TEXT,
                     parsed_response TEXT,
                     error_message  TEXT,
                     order_id       INTEGER,
                     created_at     TEXT NOT NULL
                 );`,
                [],
                true,
        )

        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_terminal_logs_created_at ON terminal_transaction_logs (created_at);`,
                [],
                true,
        )

        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_terminal_logs_provider ON terminal_transaction_logs (provider);`,
                [],
                true,
        )

        await migrateDatabase()

        // Indices on local_orders. The hot read paths
        // (getLocalOrder, getUnsyncedOrders, getAllLocalOrders by status,
        // pullOrdersFromBackend) all did full table scans without these.
        // For a busy restaurant with thousands of historical orders this
        // turns into seconds-of-stalled-UI. IF NOT EXISTS makes them safe
        // to add to the boot path on existing installs.
        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_local_orders_backend_id ON local_orders (backend_order_id);`,
                [],
                true,
        )
        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_local_orders_status_created ON local_orders (status, created_at DESC);`,
                [],
                true,
        )
        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_local_orders_guest_session ON local_orders (guest_session_id);`,
                [],
                true,
        )
        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_local_orders_customer ON local_orders (customer_id);`,
                [],
                true,
        )
        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_local_orders_show_on ON local_orders (show_on);`,
                [],
                true,
        )
        // Partial index for "needs syncing" queries — only stores rows
        // where synced_at is NULL, which is exactly what getUnsyncedOrders
        // wants. Tiny, hot.
        await executeQuery(
                `CREATE INDEX IF NOT EXISTS idx_local_orders_unsynced ON local_orders (sync_attempts) WHERE synced_at IS NULL;`,
                [],
                true,
        )
    }

    async function migrateDatabase() {
        console.log('Running database migrations...')

        try {
            const columns = await selectQuery(
                    `PRAGMA table_info(local_orders)`,
                    [],
                    true
            )

            const columnNames = columns.map(col => col.name)

            const requiredColumns = [
                // Customer fields
                {name: 'customer_email', type: 'TEXT'},
                {name: 'customer_first_name', type: 'TEXT'},
                {name: 'customer_last_name', type: 'TEXT'},

                // Delivery fields
                {name: 'delivery_address_line1', type: 'TEXT'},
                {name: 'delivery_address_line2', type: 'TEXT'},
                {name: 'delivery_city', type: 'TEXT'},
                {name: 'delivery_state', type: 'TEXT'},
                {name: 'delivery_zip_code', type: 'TEXT'},
                {name: 'delivery_country', type: 'TEXT'},
                {name: 'delivery_latitude', type: 'REAL'},
                {name: 'delivery_longitude', type: 'REAL'},
                {name: 'delivery_distance_km', type: 'REAL'},
                {name: 'delivery_fee', type: 'REAL DEFAULT 0'},
                {name: 'delivery_notes_for_driver', type: 'TEXT'},
                {name: 'delivery_provider', type: 'TEXT'},
                {name: 'delivery_quote_id', type: 'TEXT'},
                {name: 'delivery_quote_fee', type: 'REAL'},
                {name: 'delivery_quote_expires_at', type: 'TEXT'},
                {name: 'delivery_tracking_url', type: 'TEXT'},

                // Financial fields
                {name: 'tax_amount', type: 'REAL DEFAULT 0'},
                {name: 'tax_rate', type: 'REAL'},
                {name: 'service_fee', type: 'REAL DEFAULT 0'},
                {name: 'payment_surcharge', type: 'REAL DEFAULT 0'},
                {name: 'actual_payment_surcharge', type: 'REAL'},
                {name: 'order_surcharge', type: 'REAL DEFAULT 0'},
                {name: 'platform_fee_applied', type: 'REAL'},

                // Payment fields
                {name: 'payment_method', type: 'TEXT'},
                {name: 'payment_status', type: "TEXT DEFAULT 'pending'"},
                {name: 'stripe_payment_intent_id', type: 'TEXT'},
                {name: 'stripe_charge_id', type: 'TEXT'},
                {name: 'last_4_digits', type: 'TEXT'},
                {name: 'card_type', type: 'TEXT'},
                {name: 'card_country', type: 'TEXT'},
                {name: 'terminal_transaction_id', type: 'TEXT'},
                {name: 'terminal_auth_code', type: 'TEXT'},
                {name: 'terminal_aid', type: 'TEXT'},
                {name: 'terminal_provider', type: 'TEXT'},

                // Order lifecycle fields
                {name: 'is_asap', type: 'INTEGER DEFAULT 1'},
                {name: 'scheduled_at', type: 'TEXT'},
                {name: 'placed_at', type: 'TEXT'},
                {name: 'confirmed_at', type: 'TEXT'},
                {name: 'ready_at', type: 'TEXT'},
                {name: 'completed_at', type: 'TEXT'},
                {name: 'cancelled_at', type: 'TEXT'},
                {name: 'cancellation_reason', type: 'TEXT'},

                // Refund fields
                {name: 'refund_status', type: 'TEXT'},
                {name: 'refund_id', type: 'TEXT'},
                {name: 'refund_amount', type: 'REAL'},
                {name: 'refund_reason', type: 'TEXT'},
                {name: 'refunded_at', type: 'TEXT'},

                // Deal/voucher fields
                {name: 'applied_deal_id', type: 'INTEGER'},
                {name: 'applied_voucher_code', type: 'TEXT'},
                {name: 'voucher_discount', type: 'REAL DEFAULT 0'},

                // Notes fields
                {name: 'customer_notes', type: 'TEXT'},
                {name: 'internal_notes', type: 'TEXT'},

                // Sync fields
                {name: 'backend_order_id', type: 'INTEGER'},
                {name: 'sync_attempts', type: 'INTEGER DEFAULT 0'},
                {name: 'last_sync_error', type: 'TEXT'},

                // Server/staff fields
                {name: 'server_id', type: 'INTEGER'},
                {name: 'server_name', type: 'TEXT'},

                // Session field
                {name: 'guest_session_id', type: 'TEXT'},

                // Online order customer fields
                {name: 'customer_id', type: 'INTEGER'},
                {name: 'account_id', type: 'INTEGER'},

                // Kitchen workflow fields
                {name: 'sent_to_kitchen_at', type: 'TEXT'},

                // Loyalty program fields
                {name: 'loyalty_customer_id', type: 'INTEGER'},
                {name: 'loyalty_customer_name', type: 'TEXT'},
                {name: 'loyalty_customer_phone', type: 'TEXT'},
                {name: 'loyalty_points_balance', type: 'INTEGER'},
                {name: 'loyalty_reward_id', type: 'INTEGER'},
                {name: 'loyalty_reward_name', type: 'TEXT'},
                {name: 'loyalty_reward_type', type: 'TEXT'},
                {name: 'loyalty_reward_value', type: 'REAL'},
                {name: 'loyalty_points_redeemed', type: 'INTEGER'},
                {name: 'loyalty_discount_applied', type: 'REAL DEFAULT 0'},
                {name: 'loyalty_reward_discount', type: 'REAL DEFAULT 0'},

                // Course fire/hold field
                {name: 'enable_course_fire_hold', type: 'INTEGER DEFAULT 0'},

                // Order source field
                // Use single quotes for the literal — SQLite treats double-
                // quoted text as an identifier first, falling back to string
                // only if the identifier doesn't resolve. Strict mode (and
                // future SQLite versions) reject the identifier interpretation
                // outright, breaking the migration. Stick to single quotes.
                {name: 'show_on', type: "TEXT DEFAULT 'POS'"},

                // Gift card fields
                {name: 'gift_card_code', type: 'TEXT'},
                {name: 'gift_card_amount', type: 'REAL DEFAULT 0'},

                // Amount charged (final amount after all discounts)
                {name: 'amount_charged', type: 'REAL'},
            ]

            for (const col of requiredColumns) {
                if (!columnNames.includes(col.name)) {
                    console.log(`Adding missing column: ${col.name}`)
                    await executeQuery(
                            `ALTER TABLE local_orders
                                ADD COLUMN ${col.name} ${col.type}`,
                            [],
                            true
                    )
                }
            }

            console.log('Database migrations completed')
        } catch (err) {
            console.error('Migration error:', err)
        }
    }

    // --- Public API Functions ---
    // Everything below is exported and used throughout the app.
    // These all use our abstraction layer, so they work identically in Tauri and web.

    // Fetches fresh business data from the API and stores it in pos_cache table
    // This includes: menu, deals, staff, floor plans, business settings
    // Called on login and when user manually refreshes
    // Why pos_cache? It allows us to work offline with the last synced data
    async function syncBusinessData(businessId) {
        if (!isDbReady.value) throw new Error('Database not initialized for sync.')
        isLoadingData.value = true
        console.log(`Syncing business data for ID: ${businessId}`)
        try {
            const {data: syncDataRef, error} = await useApi(`/api/pos/sync-data/${businessId}`)
            if (error.value) throw error.value
            const syncData = syncDataRef.value
            if (!syncData) throw new Error('No data returned from sync endpoint.')

            // Atomic refresh via INSERT OR REPLACE (cache table has key as PK).
            // Previously a DELETE-then-INSERT race let any concurrent reader
            // see an EMPTY pos_cache between the two statements — POS / kiosk
            // pages briefly rendered blank menus during sync. INSERT OR
            // REPLACE keeps each key valid throughout the swap; tables stay
            // populated with the old value until the new one lands.
            const now = new Date().toISOString()
            const upsertPromises = []

            const upsert = (key, value) => upsertPromises.push(
                executeQuery(
                    "INSERT INTO pos_cache (key, value, synced_at) VALUES (?, ?, ?) " +
                    "ON CONFLICT(key) DO UPDATE SET value = excluded.value, synced_at = excluded.synced_at",
                    [key, JSON.stringify(value), now],
                    true,
                ),
            )

            if (syncData.menu) upsert('activeMenu', syncData.menu)
            if (syncData.allMenus) upsert('allMenus', syncData.allMenus)
            if (syncData.deals) upsert('activeDeals', syncData.deals)
            if (syncData.staff) upsert('staffList', syncData.staff)
            if (syncData.floorPlans) upsert('floorPlans', syncData.floorPlans)
            if (syncData.business) upsert('business', syncData.business)
            if (syncData.courses) upsert('courses', syncData.courses)

            await Promise.all(upsertPromises)
            console.log('Business data successfully synced to local DB.')

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('pos-data-synced', {detail: {businessId}}))
            }
        } catch (err) {
            console.error('Failed during business data sync:', err)
            throw err
        } finally {
            isLoadingData.value = false
        }
    }

    // Retrieves data from pos_cache that was stored by syncBusinessData()
    // Returns parsed JSON or null if not found
    // Used by: menu pages, deal calculations, staff lists, floor plan displays
    async function getCachedData(key) {
        if (!isDbReady.value) return null
        const result = await selectQuery("SELECT value FROM pos_cache WHERE key = ?", [key])
        if (result && result.length > 0 && result[0].value) {
            try {
                return JSON.parse(result[0].value)
            } catch (e) {
                console.error(`Failed to parse cached JSON for key ${key}:`, e)

                return null
            }
        }

        return null
    }

    // Stores arbitrary cacheable data (slideshows, etc.) in pos_cache. Symmetric
    // to getCachedData — the value is JSON.stringified so reads can round-trip.
    // Use this (NOT savePosSetting) for any structured cache data.
    async function saveCachedData(key, value) {
        if (!isDbReady.value) return
        const now = new Date().toISOString()
        await executeQuery(
            "INSERT OR REPLACE INTO pos_cache (key, value, synced_at) VALUES (?, ?, ?)",
            [key, JSON.stringify(value), now],
        )
    }

    // Keys whose stored values must be sealed at rest. Anything matching
    // either an exact entry or a prefix entry goes through useSecretStore
    // before the SQLite write and on the way back out. The set is
    // intentionally narrow — sealing every config row would balloon storage
    // and force every boot to round-trip through the OS keyring N times.
    //   - terminalConfig : full Tyro/Stripe/Windcave terminal config including
    //                      apiKey, integrationKey, secretKey
    //   - tyro_paired_*  : per-terminal pairing flag (the integration key
    //                      itself lives in terminalConfig but the pairing
    //                      indicator is still business-identifying)
    // Other plausibly-sensitive keys we deliberately leave plaintext:
    //   - tyro_pending_transaction lives in localStorage and only contains
    //     {orderId, amount, timestamp} (no receipts/PAN), so not sealed here.
    //   - paymentProvider is just the provider name string ("tyro"/"stripe").
    const SEALED_KEY_EXACT = new Set([
        'terminalConfig',
    ])
    const SEALED_KEY_PREFIXES = ['tyro_paired_']

    function shouldSealKey(key) {
        if (SEALED_KEY_EXACT.has(key)) return true
        for (const prefix of SEALED_KEY_PREFIXES) {
            if (key.startsWith(prefix)) return true
        }
        return false
    }

    // Stores a config value (like isKdsMode, isTabletMode, selectedPrinter, etc.)
    // These persist across app restarts. Sensitive keys (see SEALED_KEY_*) are
    // sealed via the OS keyring + AES-GCM before write.
    async function savePosSetting(key, value) {
        let toStore = value
        if (shouldSealKey(key) && value != null && value !== '') {
            const { useSecretStore } = await import('./useSecretStore.js')
            toStore = await useSecretStore().seal(String(value))
        }
        await executeQuery("INSERT OR REPLACE INTO config (key, value) VALUES (?, ?)", [key, toStore])
    }

    // Retrieves a config value, returns null if not found.
    async function getPosSetting(key) {
        const result = await selectQuery("SELECT value FROM config WHERE key = ?", [key])
        if (result.length === 0) return null

        const raw = result[0].value
        if (shouldSealKey(key) && raw != null && raw !== '') {
            const { useSecretStore } = await import('./useSecretStore.js')
            return await useSecretStore().unseal(raw)
        }
        return raw
    }

    async function deletePosSetting(key) {
        await executeQuery("DELETE FROM config WHERE key = ?", [key])
    }

    // One-shot migration sweep — runs at startup. Walks the config table and
    // proactively reseals any rows that match the SEALED_KEY set but whose
    // stored value still looks like plaintext (legacy installs from before
    // sealing was introduced).
    //
    // We detect "plaintext" heuristically:
    //   - The value isn't empty.
    //   - The value either fails base64 decode entirely, OR decodes to fewer
    //     than (1 + 12 + 16) bytes (version + nonce + GCM tag minimum), OR
    //     starts with a JSON-like prefix (`{`, `[`, `"`).
    // Sealed values are base64 strings whose first decoded byte is the SEAL
    // version (currently 1) — so a JSON `{` (0x7B) decoded blob is a strong
    // tell that we're looking at a legacy unsealed row.
    //
    // Errors don't abort startup — sealing is best-effort. Bad rows get
    // logged so support can investigate without breaking the app.
    async function migrateLegacyPlaintextRows() {
        let resealed = 0
        try {
            const sealableKeys = ['terminalConfig']
            // Prefix scan: pull all rows whose key starts with any sealed prefix.
            const prefixWhere = SEALED_KEY_PREFIXES.map(() => "key LIKE ?").join(' OR ')
            const prefixPlaceholders = SEALED_KEY_PREFIXES.map((p) => `${p}%`)
            const exactPlaceholders = sealableKeys.map(() => '?').join(',')
            const sql = `SELECT key, value FROM config WHERE key IN (${exactPlaceholders}) OR ${prefixWhere}`
            const rows = await selectQuery(sql, [...sealableKeys, ...prefixPlaceholders])

            const { useSecretStore } = await import('./useSecretStore.js')
            const { seal } = useSecretStore()

            for (const row of rows) {
                const value = row.value
                if (!value || typeof value !== 'string') continue
                if (looksLikeSealedCiphertext(value)) continue

                try {
                    const sealed = await seal(value)
                    if (sealed && sealed !== value) {
                        await executeQuery(
                            "UPDATE config SET value = ? WHERE key = ?",
                            [sealed, row.key]
                        )
                        resealed++
                    }
                } catch (err) {
                    console.warn('[posDB] migration: failed to reseal', row.key, err)
                }
            }
            if (resealed > 0) {
                console.log(`[posDB] migration: resealed ${resealed} legacy plaintext row(s)`)
            }
        } catch (err) {
            console.warn('[posDB] migration: sweep failed (non-fatal)', err)
        }
        return resealed
    }

    // Cheap test: try to base64-decode and check the first byte against
    // SEAL_VERSION (1). Sealed payloads decode to >=29 bytes (1 + 12 + 16).
    function looksLikeSealedCiphertext(value) {
        if (!value || typeof value !== 'string') return false
        // JSON / text gives itself away early.
        const first = value.charAt(0)
        if (first === '{' || first === '[' || first === '"') return false
        try {
            const bin = atob(value)
            if (bin.length < 29) return false
            // First byte is the seal version; currently 1.
            return bin.charCodeAt(0) === 1
        } catch {
            return false
        }
    }

    async function getConfiguredPrinters() {
        return await selectQuery("SELECT * FROM configured_printers ORDER BY friendly_name")
    }

    async function saveConfiguredPrinter(printer) {
        // Normalize is_enabled to the string form ('true'/'false') that read sites check via
        // `p.is_enabled === 'true'` (usePrinter.js, orders.vue, etc.). Vuetify VSwitch in the
        // edit form produces a real boolean, so without coercion edits silently disable printers.
        const enabledStr = (printer.is_enabled === true || printer.is_enabled === 'true' || printer.is_enabled === 1) ? 'true' : 'false'
        if (printer.id) {
            await executeQuery(
                    `UPDATE configured_printers
                     SET friendly_name = ?,
                         system_name   = ?,
                         destination   = ?,
                         is_enabled    = ?
                     WHERE id = ?`,
                    [printer.friendly_name, printer.system_name, printer.destination, enabledStr, printer.id],
            )
        } else {
            await executeQuery(
                    `INSERT INTO configured_printers (friendly_name, system_name, destination, is_enabled)
                     VALUES (?, ?, ?, ?)`,
                    [printer.friendly_name, printer.system_name, printer.destination, enabledStr],
            )
        }
    }

    async function deleteConfiguredPrinter(id) {
        await executeQuery("DELETE FROM configured_printers WHERE id = ?", [id])
    }

    async function getScaleConfig() {
        const result = await selectQuery("SELECT * FROM scale_config WHERE id = 1 LIMIT 1")
        if (result.length > 0) {
            return {
                enabled: Boolean(result[0].enabled),
                deviceName: result[0].device_name || '',
                port: result[0].port || '',
                baudRate: result[0].baud_rate || 9600,
                protocol: result[0].protocol || 'Standard',
                unit: result[0].unit || 'kg',
            }
        }
        return {
            enabled: false,
            deviceName: '',
            port: '',
            baudRate: 9600,
            protocol: 'Standard',
            unit: 'kg',
        }
    }

    async function saveScaleConfig(config) {
        const existingConfig = await selectQuery("SELECT id FROM scale_config WHERE id = 1")

        if (existingConfig.length > 0) {
            await executeQuery(
                    `UPDATE scale_config
                     SET enabled     = ?,
                         device_name = ?,
                         port        = ?,
                         baud_rate   = ?,
                         protocol    = ?,
                         unit        = ?
                     WHERE id = 1`,
                    [
                        config.enabled ? 1 : 0,
                        config.deviceName || '',
                        config.port || '',
                        config.baudRate || 9600,
                        config.protocol || 'Standard',
                        config.unit || 'kg',
                    ]
            )
        } else {
            await executeQuery(
                    `INSERT INTO scale_config (id, enabled, device_name, port, baud_rate, protocol, unit)
                     VALUES (1, ?, ?, ?, ?, ?, ?)`,
                    [
                        config.enabled ? 1 : 0,
                        config.deviceName || '',
                        config.port || '',
                        config.baudRate || 9600,
                        config.protocol || 'Standard',
                        config.unit || 'kg',
                    ]
            )
        }
    }

    // Saves or updates an order in our local database (the offline-first source of truth)
    // This is called:
    // - When creating a new order (generates a local ID)
    // - When modifying an existing order (items, status, etc.)
    // - After successfully syncing to backend (to store backendOrderId)
    //
    // PROPERTY MAPPING (Backend Format):
    // - order.orderDetails → order_details column (JSON stringified array)
    // - Each OrderDetail object contains:
    //   * menuItemName (string) - Item name
    //   * unitPrice (number) - Price per unit
    //   * itemSubtotal (number) - Total for this line (unitPrice + modifiers) * quantity
    //   * orderDetailModifiers (array) - Applied modifiers with modifierGroupName, modifierOptionName, appliedModType, priceAdjustment
    async function saveLocalOrder(order) {
        console.log('[saveLocalOrder] Called with order:', {
            id: order.id,
            orderNumber: order.orderNumber,
            backendOrderId: order.backendOrderId || order.backend_order_id,
            hasOrderDetails: 'orderDetails' in order,
            hasItems: 'items' in order,
            orderDetailsLength: order.orderDetails?.length,
            itemsLength: order.items?.length,
            orderDetailsType: typeof order.orderDetails,
            itemsType: typeof order.items,
            order: order
        })

        const now = new Date().toISOString()
        // Always store local_orders.id as STRING. The previous coercion
        // (`Number.isFinite(Number(x)) ? Number(x) : x`) silently converted
        // numeric strings ('123') to int (123) but left non-numeric IDs
        // ('pos_1_173...') as strings — so the same logical row could exist
        // both as INTEGER and TEXT depending on which code path saved it,
        // breaking PK uniqueness and producing duplicate rows.
        const orderId = String(
            order.id || `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        )

        // Deduplication: prioritise the primary key, then fall back to other
        // identifiers in order (backend_order_id, then guest_session_id). The
        // previous single-query OR-everything lookup could match an unrelated
        // row when, e.g., the inbound `orderId` (a numeric id) collided with an
        // unrelated row's backend_order_id, silently rebinding `order.id` to
        // the wrong row.
        const backendId = Number(order.backendOrderId || order.backend_order_id)
        const hasBackendId = Number.isFinite(backendId) && backendId > 0
        const guestSessionId = order.posLocalId || order.guestSessionId

        let existing = await selectQuery(
                `SELECT * FROM local_orders
                 WHERE id = ? OR CAST(id AS INTEGER) = ?
                 LIMIT 1`,
                [orderId, orderId]
        )

        if ((!existing || existing.length === 0) && hasBackendId) {
            existing = await selectQuery(
                    `SELECT * FROM local_orders
                     WHERE backend_order_id IS NOT NULL AND backend_order_id = ?
                     LIMIT 1`,
                    [backendId]
            )
        }

        if ((!existing || existing.length === 0) && guestSessionId) {
            existing = await selectQuery(
                    `SELECT * FROM local_orders
                     WHERE guest_session_id IS NOT NULL AND guest_session_id = ?
                     LIMIT 1`,
                    [guestSessionId]
            )
        }

        if (existing && existing.length > 0) {
            console.log('[saveLocalOrder] Found existing order:', {
                matchedById: existing[0].id === orderId,
                matchedByBackendId: existing[0].backend_order_id === backendId,
                matchedByGuestSessionId: existing[0].guest_session_id === guestSessionId,
                existingId: existing[0].id,
                backendId,
                guestSessionId
            })
            // Use the existing order's ID to update it
            order.id = existing[0].id
        }

        if (existing && existing.length > 0) {
            // Update existing order - only update fields that are provided
            const updates = []
            const values = []

            // Helper to add field if it has a value
            const addField = (dbField, value) => {
                if (value !== undefined && value !== null) {
                    updates.push(`${dbField} = ?`)
                    values.push(value)
                }
            }

            // Only update fields that are actually present in the order object
            if ('orderNumber' in order) addField('order_number', order.orderNumber)
            if ('orderType' in order) addField('order_type', order.orderType)
            if ('tableId' in order) addField('table_id', order.tableId)
            if ('tableName' in order) addField('table_name', order.tableName)
            if ('floorPlanId' in order) addField('floor_plan_id', order.floorPlanId)
            if ('customerType' in order) addField('customer_type', order.customerType)
            if ('customerName' in order) addField('customer_name', order.customerName)
            if ('customerPhone' in order) addField('customer_phone', order.customerPhone)
            if ('customerEmail' in order) addField('customer_email', order.customerEmail)
            if ('customerFirstName' in order) addField('customer_first_name', order.customerFirstName)
            if ('customerLastName' in order) addField('customer_last_name', order.customerLastName)
            if ('deliveryAddressLine1' in order) addField('delivery_address_line1', order.deliveryAddressLine1)
            if ('deliveryAddressLine2' in order) addField('delivery_address_line2', order.deliveryAddressLine2)
            if ('deliveryCity' in order) addField('delivery_city', order.deliveryCity)
            if ('deliveryState' in order) addField('delivery_state', order.deliveryState)
            if ('deliveryZipCode' in order) addField('delivery_zip_code', order.deliveryZipCode)
            if ('deliveryCountry' in order) addField('delivery_country', order.deliveryCountry)
            if ('deliveryLatitude' in order) addField('delivery_latitude', order.deliveryLatitude)
            if ('deliveryLongitude' in order) addField('delivery_longitude', order.deliveryLongitude)
            if ('deliveryDistanceKm' in order) addField('delivery_distance_km', order.deliveryDistanceKm)
            if ('deliveryFee' in order) addField('delivery_fee', order.deliveryFee)
            if ('deliveryNotesForDriver' in order) addField('delivery_notes_for_driver', order.deliveryNotesForDriver)
            if ('orderDetails' in order || 'items' in order) {
                addField('order_details', JSON.stringify(order.orderDetails || order.items || []))
            }
            if ('appliedDeals' in order) addField('applied_deals', JSON.stringify(order.appliedDeals || []))
            if ('discount' in order) addField('discount', order.discount)
            if ('discountAmount' in order) addField('discount', order.discountAmount)
            if ('discountType' in order) addField('discount_type', order.discountType)
            if ('discountValue' in order) addField('discount_value', order.discountValue)
            if ('tip' in order) addField('tip', order.tip)
            if ('tipAmount' in order) addField('tip', order.tipAmount)
            if ('tipPercent' in order) addField('tip_percent', order.tipPercent)
            if ('taxAmount' in order) addField('tax_amount', order.taxAmount)
            if ('taxRate' in order) addField('tax_rate', order.taxRate)
            if ('serviceFee' in order) addField('service_fee', order.serviceFee)
            if ('paymentSurcharge' in order) addField('payment_surcharge', order.paymentSurcharge)
            if ('actualPaymentSurcharge' in order) addField('actual_payment_surcharge', order.actualPaymentSurcharge)
            if ('orderSurcharge' in order) addField('order_surcharge', order.orderSurcharge)
            if ('platformFeeApplied' in order) addField('platform_fee_applied', order.platformFeeApplied)
            if ('notes' in order) addField('notes', order.notes)
            if ('customerNotes' in order) addField('customer_notes', order.customerNotes)
            if ('internalNotes' in order) addField('internal_notes', order.internalNotes)
            if ('subtotal' in order) addField('subtotal', order.subtotal)
            if ('total' in order) addField('total', order.total)
            if ('totalAmount' in order) addField('total', order.totalAmount)
            if ('status' in order) addField('status', order.status)
            if ('isOnlineOrder' in order) addField('is_online_order', order.isOnlineOrder ? 1 : 0)
            if ('serverId' in order) addField('server_id', order.serverId)
            if ('serverName' in order) addField('server_name', order.serverName)
            if ('paymentMethod' in order) addField('payment_method', order.paymentMethod)
            if ('paymentMethodType' in order) addField('payment_method', order.paymentMethodType)
            if ('paymentStatus' in order) addField('payment_status', order.paymentStatus)
            if ('isExternal' in order) addField('is_online_order', order.isExternal ? 1 : 0)
            // External-platform tags belong in internal_notes (operator-facing
            // staff notes), NOT in `notes` (the customer-supplied / customer-
            // visible field). Previously this overwrote the customer's notes
            // with the literal string "External: uber_eats" — a customer
            // saying "no onions please" lost their note as soon as a partial
            // sync from the external provider arrived. Append to existing
            // internal_notes so we don't trample externalOrderId either.
            if ('externalPlatform' in order && order.externalPlatform) {
                const existingInternal = order.internalNotes || order.internal_notes || ''
                const tag = `External: ${order.externalPlatform}`
                const merged = existingInternal && !existingInternal.includes(tag)
                    ? `${existingInternal} | ${tag}`
                    : (existingInternal || tag)
                addField('internal_notes', merged)
            }
            if ('externalOrderId' in order && order.externalOrderId) {
                const existingInternal = order.internalNotes || order.internal_notes || ''
                const tag = `External Order ID: ${order.externalOrderId}`
                const merged = existingInternal && !existingInternal.includes(tag)
                    ? `${existingInternal} | ${tag}`
                    : (existingInternal || tag)
                addField('internal_notes', merged)
            }
            if ('stripePaymentIntentId' in order) addField('stripe_payment_intent_id', order.stripePaymentIntentId)
            if ('stripeChargeId' in order) addField('stripe_charge_id', order.stripeChargeId)
            if ('last4Digits' in order) addField('last_4_digits', order.last4Digits)
            if ('cardType' in order) addField('card_type', order.cardType)
            if ('cardCountry' in order) addField('card_country', order.cardCountry)
            if ('terminalTransactionId' in order) addField('terminal_transaction_id', order.terminalTransactionId)
            if ('terminalAuthCode' in order) addField('terminal_auth_code', order.terminalAuthCode)
            if ('terminalAid' in order) addField('terminal_aid', order.terminalAid)
            if ('terminalProvider' in order) addField('terminal_provider', order.terminalProvider)
            if ('isAsap' in order) addField('is_asap', order.isAsap ? 1 : 0)
            if ('scheduledAt' in order) addField('scheduled_at', order.scheduledAt)
            if ('placedAt' in order) addField('placed_at', order.placedAt)
            if ('confirmedAt' in order) addField('confirmed_at', order.confirmedAt)
            if ('readyAt' in order) addField('ready_at', order.readyAt)
            if ('completedAt' in order) addField('completed_at', order.completedAt)
            if ('cancelledAt' in order) addField('cancelled_at', order.cancelledAt)
            if ('cancellationReason' in order) addField('cancellation_reason', order.cancellationReason)
            if ('refundStatus' in order) addField('refund_status', order.refundStatus)
            if ('refundId' in order) addField('refund_id', order.refundId)
            if ('refundAmount' in order) addField('refund_amount', order.refundAmount)
            if ('refundReason' in order) addField('refund_reason', order.refundReason)
            if ('refundedAt' in order) addField('refunded_at', order.refundedAt)
            if ('appliedDealId' in order) addField('applied_deal_id', order.appliedDealId)
            if ('appliedVoucherCode' in order) addField('applied_voucher_code', order.appliedVoucherCode)
            if ('voucherDiscount' in order) addField('voucher_discount', order.voucherDiscount)
            if ('createdAt' in order) addField('created_at', order.createdAt)
            if ('updatedAt' in order) addField('updated_at', order.updatedAt)
            if ('deliveredAt' in order) addField('delivered_at', order.deliveredAt)
            if ('pickedUpAt' in order) addField('picked_up_at', order.pickedUpAt)
            if ('deliveryProvider' in order) addField('delivery_provider', order.deliveryProvider)
            if ('deliveryQuoteId' in order) addField('delivery_quote_id', order.deliveryQuoteId)
            if ('deliveryQuoteFee' in order) addField('delivery_quote_fee', order.deliveryQuoteFee)
            if ('deliveryQuoteExpiresAt' in order) addField('delivery_quote_expires_at', order.deliveryQuoteExpiresAt)
            if ('deliveryTrackingUrl' in order) addField('delivery_tracking_url', order.deliveryTrackingUrl)

            // Handle both camelCase and snake_case for backend_order_id
            // IMPORTANT: backend_order_id must ALWAYS be a numeric PostgreSQL ID, never a posLocalId string
            if ('backendOrderId' in order || 'backend_order_id' in order) {
                const backendOrderId = order.backendOrderId || order.backend_order_id
                // Only save if it's a valid number (not NaN, not a string like "local_...")
                if (typeof backendOrderId === 'number' && !Number.isNaN(backendOrderId) && backendOrderId > 0) {
                    addField('backend_order_id', backendOrderId)
                } else if (typeof backendOrderId === 'string' && !backendOrderId.startsWith('local_')) {
                    // If it's a numeric string (from API), convert to number
                    const numericId = Number(backendOrderId)
                    if (!Number.isNaN(numericId) && numericId > 0) {
                        addField('backend_order_id', numericId)
                    }
                }
                // If backendOrderId is a posLocalId string (starts with "local_"), ignore it - don't save
            }

            // Handle both camelCase and snake_case for synced_at
            if ('syncedAt' in order || 'synced_at' in order) {
                addField('synced_at', order.syncedAt || order.synced_at)
            }

            // Handle both camelCase and snake_case for sent_to_kitchen_at
            if ('sentToKitchenAt' in order || 'sent_to_kitchen_at' in order) {
                addField('sent_to_kitchen_at', order.sentToKitchenAt || order.sent_to_kitchen_at)
            }

            // Loyalty program fields
            if ('loyaltyCustomerId' in order) addField('loyalty_customer_id', order.loyaltyCustomerId)
            if ('loyaltyCustomerName' in order) addField('loyalty_customer_name', order.loyaltyCustomerName)
            if ('loyaltyCustomerPhone' in order) addField('loyalty_customer_phone', order.loyaltyCustomerPhone)
            if ('loyaltyPointsBalance' in order) addField('loyalty_points_balance', order.loyaltyPointsBalance)
            if ('loyaltyRewardId' in order) addField('loyalty_reward_id', order.loyaltyRewardId)
            if ('loyaltyRewardName' in order) addField('loyalty_reward_name', order.loyaltyRewardName)
            if ('loyaltyRewardType' in order) addField('loyalty_reward_type', order.loyaltyRewardType)
            if ('loyaltyRewardValue' in order) addField('loyalty_reward_value', order.loyaltyRewardValue)
            if ('loyaltyPointsRedeemed' in order) addField('loyalty_points_redeemed', order.loyaltyPointsRedeemed)
            if ('loyaltyRewardDiscount' in order) {
                addField('loyalty_discount_applied', order.loyaltyRewardDiscount)
                addField('loyalty_reward_discount', order.loyaltyRewardDiscount)
            } else if ('loyaltyDiscountApplied' in order) {
                addField('loyalty_discount_applied', order.loyaltyDiscountApplied)
                addField('loyalty_reward_discount', order.loyaltyDiscountApplied)
            }

            // Course fire/hold field
            if ('enableCourseFireHold' in order) addField('enable_course_fire_hold', order.enableCourseFireHold ? 1 : 0)

            // Priority field
            if ('priority' in order) addField('priority', order.priority || 'normal')

            // Online order customer fields
            if ('customerId' in order || 'customer_id' in order) addField('customer_id', order.customerId || order.customer_id)
            if ('accountId' in order || 'account_id' in order) addField('account_id', order.accountId || order.account_id)

            // Order source field
            if ('showOn' in order || 'show_on' in order) addField('show_on', order.showOn || order.show_on)

            // Gift card fields
            if ('giftCardCode' in order) addField('gift_card_code', order.giftCardCode)
            if ('giftCardAmount' in order) addField('gift_card_amount', order.giftCardAmount)

            // Amount charged
            if ('amountCharged' in order) addField('amount_charged', order.amountCharged)

            // Always update updated_at
            updates.push('updated_at = ?')
            values.push(now)

            // The lookup above resolved `order.id` to the existing row's id, so
            // we can update by primary key only — no OR fallback. This avoids
            // accidentally updating multiple rows when the row's id, its
            // backend_order_id, and the supplied identifiers don't all line up.
            const targetId = existing[0].id
            values.push(targetId)

            if (updates.length > 0) {
                await executeQuery(
                        `UPDATE local_orders
                         SET ${updates.join(', ')}
                         WHERE id = ?`,
                        values
                )
            }
        } else {
            // Insert new order with all required fields
            // Note: order_details column stores backend format (orderDetails property)

            // Generate order number if not provided (NOT NULL constraint)
            // Order number can be alphanumeric (e.g., "T5-MJ47KEUR" for table tabs)
            const orderNumber = order.orderNumber || order.order_number || order.id || Date.now()

            // For orders coming from backend (online orders), mark as already synced
            const isFromBackend = order.isOnlineOrder || order.backendOrderId || order.backend_order_id
            const syncedAt = isFromBackend ? (order.syncedAt || order.synced_at || now) : (order.syncedAt || order.synced_at || null)

            // INSERT path — used for brand-new orders. Previously this
            // bound only ~59 columns and silently dropped tax / service-fee /
            // surcharge / terminal-payment / customer-notes / delivery-quote
            // fields. Brand-new offline orders that were synced before any
            // re-save shipped to the backend with NULLs in those columns,
            // breaking sales reports and reconciliation.
            await executeQuery(
                    `INSERT INTO local_orders
                     (id, order_number, order_type, table_id, table_name, floor_plan_id,
                      customer_type, customer_name, customer_phone, customer_email,
                      delivery_address_line1, delivery_address_line2, delivery_city, delivery_state, delivery_zip_code, delivery_country,
                      delivery_latitude, delivery_longitude, delivery_distance_km, delivery_fee,
                      delivery_provider, delivery_quote_id, delivery_quote_fee, delivery_quote_expires_at, delivery_tracking_url,
                      delivery_notes_for_driver,
                      order_details, applied_deals, applied_voucher_code, voucher_discount,
                      discount, discount_type, discount_value, tip, tip_percent, notes,
                      customer_notes, internal_notes,
                      tax_amount, tax_rate, service_fee,
                      payment_surcharge, actual_payment_surcharge, order_surcharge, platform_fee_applied,
                      subtotal, total, status, priority, is_online_order, server_id, server_name, payment_method,
                      payment_status,
                      stripe_payment_intent_id, stripe_charge_id,
                      last_4_digits, card_type, card_country,
                      terminal_transaction_id, terminal_auth_code, terminal_aid, terminal_provider,
                      backend_order_id, synced_at, sent_to_kitchen_at,
                      loyalty_customer_id, loyalty_customer_name, loyalty_customer_phone, loyalty_points_balance,
                      loyalty_reward_id, loyalty_reward_name, loyalty_reward_type, loyalty_reward_value,
                      loyalty_points_redeemed, loyalty_discount_applied, loyalty_reward_discount,
                      enable_course_fire_hold, show_on, gift_card_code, gift_card_amount, amount_charged,
                      created_at, updated_at)
                     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?,
                             ?, ?, ?, ?, ?, ?,
                             ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?,
                             ?, ?, ?, ?, ?, ?, ?, ?, ?,
                             ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                    [
                        orderId,
                        orderNumber,
                        order.orderType || 'pickup',
                        order.tableId || null,
                        order.tableName || null,
                        order.floorPlanId || null,
                        order.customerType || null,
                        order.customerName || null,
                        order.customerPhone || null,
                        order.customerEmail || null,
                        order.deliveryAddressLine1 || null,
                        order.deliveryAddressLine2 || null,
                        order.deliveryCity || null,
                        order.deliveryState || null,
                        order.deliveryZipCode || null,
                        order.deliveryCountry || null,
                        order.deliveryLatitude || null,
                        order.deliveryLongitude || null,
                        order.deliveryDistanceKm || null,
                        order.deliveryFee || 0,
                        order.deliveryProvider || null,
                        order.deliveryQuoteId || null,
                        order.deliveryQuoteFee ?? null,
                        order.deliveryQuoteExpiresAt || null,
                        order.deliveryTrackingUrl || null,
                        order.deliveryNotesForDriver || null,
                        JSON.stringify(order.orderDetails || order.items || []),
                        JSON.stringify(order.appliedDeals || []),
                        order.appliedVoucherCode || null,
                        order.voucherDiscount || 0,
                        order.discount || 0,
                        order.discountType || null,
                        order.discountValue || 0,
                        order.tip || 0,
                        order.tipPercent || 0,
                        order.notes || null,
                        order.customerNotes || null,
                        order.internalNotes || null,
                        order.taxAmount ?? 0,
                        order.taxRate ?? null,
                        order.serviceFee ?? 0,
                        order.paymentSurcharge ?? 0,
                        order.actualPaymentSurcharge ?? null,
                        order.orderSurcharge ?? 0,
                        order.platformFeeApplied ?? null,
                        order.subtotal || 0,
                        order.total || 0,
                        order.status || 'draft',
                        order.priority || 'normal',
                        order.isOnlineOrder ? 1 : 0,
                        order.serverId || null,
                        order.serverName || null,
                        order.paymentMethod || null,
                        order.paymentStatus || 'pending',
                        order.stripePaymentIntentId || null,
                        order.stripeChargeId || null,
                        order.last4Digits || null,
                        order.cardType || null,
                        order.cardCountry || null,
                        order.terminalTransactionId || null,
                        order.terminalAuthCode || null,
                        order.terminalAid || null,
                        order.terminalProvider || null,
                        order.backendOrderId || order.backend_order_id || null,
                        syncedAt,
                        order.sentToKitchenAt || order.sent_to_kitchen_at || null,
                        order.loyaltyCustomerId || null,
                        order.loyaltyCustomerName || null,
                        order.loyaltyCustomerPhone || null,
                        order.loyaltyPointsBalance || null,
                        order.loyaltyRewardId || null,
                        order.loyaltyRewardName || null,
                        order.loyaltyRewardType || null,
                        order.loyaltyRewardValue || null,
                        order.loyaltyPointsRedeemed || null,
                        order.loyaltyRewardDiscount || order.loyaltyDiscountApplied || 0,
                        order.loyaltyRewardDiscount || order.loyaltyDiscountApplied || 0,
                        order.enableCourseFireHold ? 1 : 0,
                        order.showOn || order.show_on || 'POS',
                        order.giftCardCode || null,
                        order.giftCardAmount || 0,
                        order.amountCharged || null,
                        order.createdAt || now,
                        now,
                    ]
            )
        }

        try {
            const orderDetails = order.orderDetails || order.items || []
            const firstItemStatus = orderDetails.length > 0 ? orderDetails[0]?.itemStatus : null
            const itemsCount = orderDetails.length

            const eventSource = new Error().stack
                    ?.split('\n')[2]
                    ?.trim()
                    ?.replace(/^at\s+/, '') || 'unknown'

            if (backendId && typeof backendId === 'number') {
                await executeQuery(
                        `DELETE
                         FROM order_save_audit_log
                         WHERE backend_order_id != ?
                           AND (backend_order_id IS NOT NULL OR local_order_id = ?)`,
                        [backendId, orderId]
                )
            }

            await executeQuery(
                    `INSERT INTO order_save_audit_log
                     (backend_order_id, local_order_id, event_source, order_details, first_item_status, items_count,
                      timestamp)
                     VALUES (?, ?, ?, ?, ?, ?, ?)`,
                    [
                        backendId || null,
                        orderId,
                        eventSource,
                        JSON.stringify(orderDetails),
                        firstItemStatus,
                        itemsCount,
                        now
                    ]
            )
        } catch (auditError) {
            console.error('[saveLocalOrder] Failed to write audit log:', auditError)
        }

        return orderId
    }

    function getOrderObject(order) {
        return {
            ...order,
            // Force Vue reactivity - timestamp ensures new object reference
            _refreshedAt: Date.now(),
            // Parse backend format: order_details column → orderDetails property
            orderDetails: JSON.parse(order.order_details || '[]'),
            // items property mirrors orderDetails for compatibility with display components
            items: JSON.parse(order.order_details || '[]'),
            appliedDeals: JSON.parse(order.applied_deals || '[]'),
            isOnlineOrder: Boolean(order.is_online_order),
            tableId: order.table_id,
            tableName: order.table_name,
            floorPlanId: order.floor_plan_id,
            orderNumber: order.order_number,
            orderType: order.order_type,
            customerType: order.customer_type,
            customerName: order.customer_name,
            customerPhone: order.customer_phone,
            customerEmail: order.customer_email,
            discount: order.discount,
            discountAmount: order.discount,
            discountType: order.discount_type,
            discountValue: order.discount_value,
            tip: order.tip,
            tipAmount: order.tip,
            tipPercent: order.tip_percent,
            subtotal: order.subtotal,
            total: order.total,
            totalAmount: order.total,
            taxAmount: order.tax_amount,
            taxRate: order.tax_rate,
            serverId: order.server_id,
            serverName: order.server_name,
            paymentStatus: order.payment_status,
            isPaid: order.payment_status === 'paid' || order.status === 'completed' || order.status === 'paid',
            paymentMethod: order.payment_method,
            terminalTransactionId: order.terminal_transaction_id,
            terminalAuthCode: order.terminal_auth_code,
            terminalAid: order.terminal_aid,
            terminalProvider: order.terminal_provider,
            last4Digits: order.last_4_digits,
            cardType: order.card_type,
            paymentSurcharge: order.payment_surcharge,
            orderSurcharge: order.order_surcharge,
            createdAt: order.created_at,
            updatedAt: order.updated_at,
            syncedAt: order.synced_at,
            backendOrderId: order.backend_order_id,
            deliveryProvider: order.delivery_provider,
            deliveryQuoteId: order.delivery_quote_id,
            deliveryQuoteFee: order.delivery_quote_fee,
            deliveryQuoteExpiresAt: order.delivery_quote_expires_at,
            deliveryTrackingUrl: order.delivery_tracking_url,
            deliveryFee: order.delivery_fee,
            deliveryAddressLine1: order.delivery_address_line1,
            deliveryCity: order.delivery_city,
            deliveryState: order.delivery_state,
            deliveryZipCode: order.delivery_zip_code,
            deliveryCountry: order.delivery_country,
            deliveryLatitude: order.delivery_latitude,
            deliveryLongitude: order.delivery_longitude,
            loyaltyCustomerId: order.loyalty_customer_id,
            loyaltyCustomerName: order.loyalty_customer_name,
            loyaltyCustomerPhone: order.loyalty_customer_phone,
            loyaltyPointsBalance: order.loyalty_points_balance,
            loyaltyRewardId: order.loyalty_reward_id,
            loyaltyRewardName: order.loyalty_reward_name,
            loyaltyRewardType: order.loyalty_reward_type,
            loyaltyRewardValue: order.loyalty_reward_value,
            loyaltyPointsRedeemed: order.loyalty_points_redeemed,
            loyaltyRewardDiscount: order.loyalty_reward_discount || order.loyalty_discount_applied || 0,
            appliedVoucherCode: order.applied_voucher_code,
            voucherDiscount: order.voucher_discount || 0,
            appliedDealId: order.applied_deal_id,
            enableCourseFireHold: order.enable_course_fire_hold === 1 || order.enableCourseFireHold === true,
            priority: order.priority || 'normal',
            customerId: order.customer_id,
            accountId: order.account_id,
            showOn: order.show_on || 'POS',
            giftCardCode: order.gift_card_code,
            giftCardAmount: order.gift_card_amount || 0,
            amountCharged: order.amount_charged,
        }
    }

    // Retrieves a single order by ID and parses its JSON fields back into objects
    // Also converts snake_case column names to camelCase for consistency with our JS code
    // Searches by both local ID and backend_order_id to handle events from KDS
    //
    // PROPERTY MAPPING (Read):
    // - order_details column → orderDetails property (parsed JSON array of OrderDetail objects)
    // - Also populates items property with same value for display component compatibility
    async function getLocalOrder(orderId) {
        let result = await selectQuery(
                `SELECT *
                 FROM local_orders
                 WHERE id = ?
                    OR CAST(id AS INTEGER) = ?
                    OR (backend_order_id IS NOT NULL AND backend_order_id = ?)
                    OR (guest_session_id IS NOT NULL AND guest_session_id = ?)
                 LIMIT 1`,
                [orderId, orderId, orderId, orderId]
        )
        if (result && result.length > 0) {
            const order = result[0]
            return getOrderObject(order)
        }
        return null
    }

    // Retrieves all orders, optionally filtered by status (draft, sent_to_kitchen, etc.)
    // Used by: orders list page, KDS, reports
    // Returns newest orders first
    //
    // PROPERTY MAPPING (Read):
    // - order_details column → orderDetails property (parsed JSON array of OrderDetail objects)
    // - Also populates items property with same value for display component compatibility
    async function getAllLocalOrders(status = null) {
        let query = "SELECT * FROM local_orders ORDER BY created_at DESC"
        let params = []

        if (status) {
            query = "SELECT * FROM local_orders WHERE status = ? ORDER BY created_at DESC"
            params = [status]
            console.log('[getAllLocalOrders] Filtering by status:', status)
        }

        const results = await selectQuery(query, params)
        console.log('[getAllLocalOrders] Found', results.length, 'orders', status ? `with status '${status}'` : '')
        return results.map(order => getOrderObject(order))
    }

    // Updates just the status field of an order
    // Used when receiving status updates from KDS or backend
    // Status values: draft, sent_to_kitchen, preparing, ready, completed, cancelled
    async function updateLocalOrderStatus(orderId, status) {
        const now = new Date().toISOString()

        console.log('[usePosDatabase] Updating order status:', {orderId, status})

        // Get order BEFORE update
        const orderBefore = await getLocalOrder(orderId)
        console.log('[usePosDatabase] Order BEFORE status update:', {
            orderId,
            oldStatus: orderBefore?.status,
            orderNumber: orderBefore?.orderNumber,
            itemCount: orderBefore?.orderDetails?.length || orderBefore?.items?.length || 0
        })
        // Try updating by local ID first
        let result = await executeQuery(
                `UPDATE local_orders
                 SET status     = ?,
                     updated_at = ?
                 WHERE id = ?
                    OR CAST(id AS INTEGER) = ?
                    OR (backend_order_id IS NOT NULL AND backend_order_id = ?)
                    OR (guest_session_id IS NOT NULL AND guest_session_id = ?) `,
                [status, now, orderId, orderId, orderId, orderId]
        )

        console.log('[usePosDatabase] Update by ID result:', result)

        // If no rows affected and orderId is numeric, try updating by backend_order_id
        // Result format is [rowsAffected, lastInsertId] from SQLite
        const rowsAffected = Array.isArray(result) ? result[0] : result?.rowsAffected
        if (rowsAffected === 0 && !isNaN(Number(orderId))) {
            console.log('[usePosDatabase] No rows affected by ID, trying backend_order_id')
            result = await executeQuery(
                    "UPDATE local_orders SET status = ?, updated_at = ? WHERE backend_order_id = ?",
                    [status, now, Number(orderId)]
            )
            console.log('[usePosDatabase] Update by backend_order_id result:', result)
        }

        // Get order AFTER update
        const orderAfter = await getLocalOrder(orderId)
        console.log('[usePosDatabase] Order AFTER status update:', {
            orderId,
            newStatus: orderAfter?.status,
            orderNumber: orderAfter?.orderNumber,
            itemCount: orderAfter?.orderDetails?.length || orderAfter?.items?.length || 0,
            updateSuccessful: orderAfter?.status === status
        })
    }

    async function updateLocalOrderPaymentStatus(orderId, paymentStatus) {
        const now = new Date().toISOString()
        console.log('[usePosDatabase] Updating order payment status:', {orderId, paymentStatus})

        const result = await executeQuery(
                `UPDATE local_orders
                 SET payment_status = ?,
                     updated_at     = ?
                 WHERE id = ?
                    OR CAST(id AS INTEGER) = ?
                    OR (backend_order_id IS NOT NULL AND backend_order_id = ?)`,
                [paymentStatus, now, orderId, orderId, orderId]
        )

        console.log('[usePosDatabase] Payment status update result:', result)
        return result
    }

    // Permanently deletes an order from local storage
    // Use with caution! Usually we just change status to 'cancelled' instead
    //
    // Strict id-first match: try the primary key first; only fall back to
    // backend_order_id / guest_session_id when no row matched by id. This avoids
    // collateral deletes when the same numeric value happens to be the `id` of
    // one row and a `backend_order_id` (or `guest_session_id`) of an unrelated
    // row — the previous OR-everything DELETE could wipe both.
    async function deleteLocalOrder(orderId) {
        if (orderId === undefined || orderId === null || orderId === '') return

        const byId = await executeQuery(
                `DELETE FROM local_orders
                 WHERE id = ? OR CAST(id AS INTEGER) = ?`,
                [orderId, orderId]
        )

        // Heuristic: if the previous query removed a row, we're done. Tauri's
        // sql plugin returns { rowsAffected }; sql.js returns nothing — fall
        // through in that case so behaviour stays the same on web mode.
        const removed = byId && typeof byId.rowsAffected === 'number'
                ? byId.rowsAffected
                : null
        if (removed && removed > 0) return

        // No id match (or unknown rowsAffected on web) — try backend_order_id.
        const byBackend = await executeQuery(
                `DELETE FROM local_orders
                 WHERE backend_order_id IS NOT NULL AND backend_order_id = ?`,
                [orderId]
        )
        const removedBackend = byBackend && typeof byBackend.rowsAffected === 'number'
                ? byBackend.rowsAffected
                : null
        if (removedBackend && removedBackend > 0) return

        // Last resort — guest_session_id (posLocalId).
        await executeQuery(
                `DELETE FROM local_orders
                 WHERE guest_session_id IS NOT NULL AND guest_session_id = ?`,
                [orderId]
        )
    }

    async function clearAllLocalOrders() {
        console.log('[POS Database] Clearing all local orders...')
        try {
            await executeQuery("DELETE FROM local_orders")
            console.log('[POS Database] ✅ All local orders cleared')
            return {success: true, message: 'All local orders deleted successfully'}
        } catch (err) {
            console.error('[POS Database] ❌ Failed to clear orders:', err)
            return {success: false, error: err.message}
        }
    }

    async function getSavedOrder(backendOrderId) {
        console.log('[POS Database] Getting saved order by backend_order_id:', backendOrderId)
        try {
            const result = await selectQuery("SELECT * FROM local_orders WHERE backend_order_id = ? OR id = ? ", [Number(backendOrderId), Number(backendOrderId)])
            if (result && result.length > 0) {
                const order = result[0]
                const parsedOrder = {
                    ...order,
                    orderDetails: JSON.parse(order.order_details || '[]'),
                    items: JSON.parse(order.order_details || '[]'),
                    appliedDeals: JSON.parse(order.applied_deals || '[]'),
                }
                console.log('[POS Database] ✅ Found order:', parsedOrder)
                return parsedOrder
            } else {
                console.log('[POS Database] ⚠️ Order not found with backend_order_id:', backendOrderId)
                return null
            }
        } catch (err) {
            console.error('[POS Database] ❌ Failed to get saved order:', err)
            return null
        }
    }

    async function printOrderObject(backendOrderId) {
        console.log('[POS Database] Printing audit log for backend_order_id:', backendOrderId)
        try {
            const result = await selectQuery(
                    "SELECT * FROM order_save_audit_log WHERE backend_order_id = ? ORDER BY timestamp ASC",
                    [Number(backendOrderId)]
            )
            console.log('='.repeat(80))
            console.log(`AUDIT LOG FOR ORDER ${backendOrderId}`)
            console.log(`Total saves: ${result.length}`)
            console.log('='.repeat(80))

            result.forEach((entry, index) => {
                console.log(`\n[${index + 1}] Saved at: ${entry.timestamp}`)
                console.log(`    Event source: ${entry.event_source}`)
                console.log(`    Local order ID: ${entry.local_order_id}`)
                console.log(`    Items count: ${entry.items_count}`)
                console.log(`    First item status: ${entry.first_item_status}`)
                const orderDetails = JSON.parse(entry.order_details || '[]')
                if (orderDetails.length > 0) {
                    console.log(`    First item full data:`, orderDetails[0])
                }
            })
            console.log('\n' + '='.repeat(80))
            return result
        } catch (err) {
            console.error('[POS Database] ❌ Failed to print audit log:', err)
            return null
        }
    }

    // Gets the next available order number for display
    // Uses timestamp-based unique number (milliseconds since epoch)
    // This guarantees uniqueness across all terminals without backend coordination
    async function getNextOrderNumber(businessId) {
        // Use current timestamp in milliseconds as order number
        // This ensures uniqueness across all terminals and is always incrementing
        const timestampOrderNumber = Date.now()
        console.log('Generated unique order number:', timestampOrderNumber)
        return timestampOrderNumber
    }

    // Optimizes the database by running VACUUM command
    async function vacuumDatabase() {
        if (!isDbReady.value) throw new Error('Database not initialized')

        if (isTauriMode.value) {
            const {invoke} = await import('@tauri-apps/api/core')
            await invoke('plugin:sql|execute', {
                db: dbInstance.value,
                query: 'VACUUM',
                values: []
            })
        } else {
            // For web mode (sql.js), vacuum is automatic on export
            // But we can still run it manually
            dbInstance.value.run('VACUUM')
        }
    }

    // Syncs a single order to the backend
    // Maps local SQLite schema to backend Order model
    // All Order model fields are now cached locally to enable full offline capability
    // Returns backend order ID on success, null on failure
    async function syncOrderToBackend(orderId) {
        try {
            const order = await getLocalOrder(orderId)
            if (!order) {
                console.error(`Order ${orderId} not found for sync`)
                return null
            }

            if (order.synced_at) {
                console.log(`Order ${orderId} already synced, skipping`)
                return order.backend_order_id
            }

            const userStore = useUserStore()
            const businessId = userStore.activeBusinessId

            const response = await useApi('/api/pos/orders', {
                idempotencyKey: order.guest_session_id || orderId,
                businessId,
                orderType: order.order_type,
                tableId: order.table_id,
                tableName: order.table_name,
                floorPlanId: order.floor_plan_id,
                customerType: order.customer_type,
                customerName: order.customer_name,
                customerPhone: order.customer_phone,
                customerEmail: order.customer_email,
                deliveryAddressLine1: order.delivery_address_line1,
                deliveryCity: order.delivery_city,
                deliveryState: order.delivery_state,
                deliveryZipCode: order.delivery_zip_code,
                deliveryCountry: order.delivery_country,
                deliveryLatitude: order.delivery_latitude,
                deliveryLongitude: order.delivery_longitude,
                deliveryDistanceKm: order.delivery_distance_km,
                deliveryFee: order.delivery_fee,
                orderDetails: typeof order.order_details === 'string' ? JSON.parse(order.order_details) : order.order_details,
                appliedDeals: order.applied_deals ? (typeof order.applied_deals === 'string' ? JSON.parse(order.applied_deals) : order.applied_deals) : null,
                discount: order.discount,
                discountType: order.discount_type,
                discountValue: order.discount_value,
                tip: order.tip,
                tipPercent: order.tip_percent,
                notes: order.notes,
                subtotal: order.subtotal,
                total: order.total,
                status: order.status,
                paymentMethodType: order.payment_method,
                paymentStatus: order.payment_status || (order.status === 'completed' ? 'paid' : 'pending'),
                isOnlineOrder: order.is_online_order === 1,
                serverId: order.server_id,
                serverName: order.server_name,
                createdAt: order.created_at,
                showOn: order.show_on || 'POS',
                enableCourseFireHold: order.enable_course_fire_hold === 1,
            }, {
                method: 'POST',
                // Local order id is the natural idempotency key — stable across
                // sync retries from the offline queue, and identifies a unique
                // logical "order created" attempt. Without this, a flaky
                // network mid-POST followed by a retry could create two
                // backend orders with two backend IDs.
                headers: { 'Idempotency-Key': String(order.guest_session_id || orderId) },
            }, 2)

            if (!response?.success) {
                throw new Error(response?.message || 'Failed to sync order')
            }

            const backendOrderId = response?.order?.id
            const backendOrderNumber = response?.order?.orderNumber || response?.order?.order_number || String(backendOrderId)
            const now = new Date().toISOString()

            await executeQuery(
                    `UPDATE local_orders
                     SET backend_order_id = ?,
                         order_number     = ?,
                         synced_at        = ?,
                         sync_attempts    = sync_attempts + 1,
                         last_sync_error  = NULL
                     WHERE id = ?`,
                    [backendOrderId, backendOrderNumber, now, orderId]
            )

            console.log(`Order ${orderId} synced successfully, backend ID: ${backendOrderId}`)
            return backendOrderId
        } catch (err) {
            console.error(`Failed to sync order ${orderId}:`, err)

            // Don't burn the retry budget on auth failures — once the user
            // re-authenticates, the order should resume syncing. We still log
            // the error so it surfaces in diagnostics.
            const status = err?.response?.status ?? err?.statusCode ?? err?.status
            const isAuthError = status === 401 || status === 403

            if (isAuthError) {
                await executeQuery(
                        `UPDATE local_orders
                         SET last_sync_error = ?
                         WHERE id = ?`,
                        [`Auth error (${status}) — will retry after re-login`, orderId]
                )
            } else {
                await executeQuery(
                        `UPDATE local_orders
                         SET sync_attempts   = sync_attempts + 1,
                             last_sync_error = ?
                         WHERE id = ?`,
                        [err.message || 'Unknown sync error', orderId]
                )
            }

            return null
        }
    }

    // Gets all unsynced orders that need to be sent to the backend
    // IMPORTANT: Excludes online orders (is_online_order=1) and orders with backend_order_id
    // as those come FROM the backend and don't need to be synced TO it
    async function getUnsyncedOrders() {
        return await selectQuery(
                `SELECT *
                 FROM local_orders
                 WHERE synced_at IS NULL
                   AND status != 'draft'
                   AND sync_attempts < 5
                   AND is_online_order = 0
                   AND backend_order_id IS NULL
                 ORDER BY created_at ASC`
        )
    }

    // Syncs all pending orders to the backend with exponential backoff
    async function syncAllPendingOrders() {
        console.log('Starting sync of all pending orders...')

        const unsyncedOrders = await getUnsyncedOrders()
        console.log(`Found ${unsyncedOrders.length} unsynced orders`)

        let successCount = 0
        let failCount = 0

        for (const order of unsyncedOrders) {
            const backoff = Math.min(1000 * Math.pow(2, order.sync_attempts || 0), 30000)
            if (backoff > 0) {
                await new Promise(resolve => setTimeout(resolve, backoff))
            }

            const backendOrderId = await syncOrderToBackend(order.id)
            if (backendOrderId) {
                successCount++
            } else {
                failCount++
            }
        }

        console.log(`Sync completed: ${successCount} success, ${failCount} failed`)
        return {successCount, failCount, total: unsyncedOrders.length}
    }

    // Order status progression (index represents priority - higher = more advanced)
    const ORDER_STATUS_PRIORITY = {
        'draft': 0,
        'pending': 1,
        'sent_to_kitchen': 2,
        'in_progress': 3,
        'ready': 4,
        'completed': 5,
        'cancelled': 6,
        'refunded': 7,
    }

    // Payment status progression
    const PAYMENT_STATUS_PRIORITY = {
        'pending': 0,
        'processing': 1,
        'paid': 2,
        'refunded': 3,
        'failed': 4,
    }

    // Check if status update is valid (forward direction only)
    function isValidStatusTransition(currentStatus, newStatus) {
        const currentPriority = ORDER_STATUS_PRIORITY[currentStatus?.toLowerCase()] ?? -1
        const newPriority = ORDER_STATUS_PRIORITY[newStatus?.toLowerCase()] ?? -1

        // Allow if new status has higher priority, or if current status is unknown
        return newPriority >= currentPriority
    }

    // Check if payment status update is valid
    function isValidPaymentStatusTransition(currentPaymentStatus, newPaymentStatus) {
        const currentPriority = PAYMENT_STATUS_PRIORITY[currentPaymentStatus?.toLowerCase()] ?? -1
        const newPriority = PAYMENT_STATUS_PRIORITY[newPaymentStatus?.toLowerCase()] ?? -1

        // Allow if new status has higher priority, or if current status is unknown
        return newPriority >= currentPriority
    }

    // Pulls recent orders from backend and stores them in local database
    async function pullOrdersFromBackend(businessId, hoursBack = 24) {
        console.log(`Pulling orders from backend (last ${hoursBack} hours)...`)

        try {
            const {data, error} = await useApi(`/api/pos/orders?businessId=${businessId}&hours=${hoursBack}`)

            if (error.value) {
                console.error('Failed to fetch orders from backend:', error.value)
                return {success: false, error: error.value}
            }

            const orders = data.value?.orders || []
            console.log(`Received ${orders.length} orders from backend`)

            let insertedCount = 0
            let updatedCount = 0
            let skippedCount = 0

            for (const order of orders) {
                try {
                    console.log(`[pullOrdersFromBackend] DB ORDER`, order)
                    console.log(`[pullOrdersFromBackend] Processing order ${order.id}, posLocalId: ${order.posLocalId}`)

                    // Check if order already exists (by backend ID match OR by posLocalId match)
                    const searchIds = [order.id.toString()]
                    if (order.posLocalId) {
                        searchIds.push(order.posLocalId)
                    } else {
                        searchIds.push(order.id.toString())
                    }

                    const existing = await selectQuery(
                            `SELECT id, status, total, payment_status
                             FROM local_orders
                             WHERE id IN (?, ?)
                                OR backend_order_id = ?
                             LIMIT 1`,
                            [searchIds[0], searchIds[1] || null, order.id]
                    )

                    console.log(`[pullOrdersFromBackend] Found ${existing.length} existing orders for order ${order.id}`)

                    const now = new Date().toISOString()

                    let originalCreatedAt = null

                    if (existing.length > 0) {
                        const currentOrder = existing[0]
                        console.log(`[pullOrdersFromBackend] Existing order found: id=${currentOrder.id}, status=${currentOrder.status}, total=${currentOrder.total}, payment_status=${currentOrder.payment_status}`)
                        console.log(`[pullOrdersFromBackend] New order data: status=${order.status}, total=${order.totalAmount}, payment_status=${order.paymentStatus}`)

                        // Preserve original created_at timestamp
                        originalCreatedAt = order.createdAt

                        // Validate status transition (only allow forward progress)
                        if (!isValidStatusTransition(currentOrder.status, order.status)) {
                            console.warn(`[pullOrdersFromBackend] SKIPPED order ${order.id}: Invalid status transition from ${currentOrder.status} to ${order.status}`)
                            skippedCount++
                            continue
                        }

                        // Validate payment status transition (only allow forward progress)
                        if (!isValidPaymentStatusTransition(currentOrder.payment_status, order.paymentStatus)) {
                            console.warn(`[pullOrdersFromBackend] SKIPPED order ${order.id}: Invalid payment status transition from ${currentOrder.payment_status} to ${order.paymentStatus}`)
                            skippedCount++
                            continue
                        }

                        // Validate financial changes (only allow if totals match or increase)
                        const currentTotal = parseFloat(currentOrder.total || 0)
                        const newTotal = parseFloat(order.totalAmount || 0)
                        const totalDifference = Math.abs(newTotal - currentTotal)

                        // If totals differ significantly and order is already paid, skip update
                        if (totalDifference > 0.01 && currentOrder.payment_status === 'paid') {
                            console.warn(`[pullOrdersFromBackend] SKIPPED order ${order.id}: Total changed from ${currentTotal} to ${newTotal} but already paid`)
                            skippedCount++
                            continue
                        }


                        // Delete local order and insert fresh with backend ID as primary key
                        console.log(`[pullOrdersFromBackend] Deleting local order ${currentOrder.id} and replacing with backend order ${order.id}`)
                        await saveLocalOrder(order)
                        console.log(`[pullOrdersFromBackend] Successfully deleted local order ${currentOrder.id}`)
                        updatedCount++
                    } else {
                        // New order from backend (e.g., online order) - insert it
                        console.log(`[pullOrdersFromBackend] Inserting new order ${order.id} (online/external order)`)
                        await saveLocalOrder(order)
                        console.log(`[pullOrdersFromBackend] Successfully inserted new order ${order.id}`)
                        insertedCount++
                    }


                } catch (err) {
                    console.error(`Failed to store order ${order.id}:`, err)
                }
            }

            console.log(`Pull completed: ${insertedCount} inserted, ${updatedCount} updated, ${skippedCount} skipped (invalid transitions)`)
            return {success: true, insertedCount, updatedCount, skippedCount, total: orders.length}
        } catch (err) {
            console.error('Failed to pull orders from backend:', err)
            return {success: false, error: err.message}
        }
    }

    function getDisplayOrderNumber(order) {
        if (!order) return '...'

        // If order has backend ID, use it
        if (order.backend_order_id || order.backendOrderId) {
            return order.backend_order_id || order.backendOrderId
        }

        // Online orders from external platforms
        if (order.id?.startsWith('online_')) {
            return order.orderNumber || order.order_number || order.id
        }

        // Local POS orders without backend ID
        if (order.id?.startsWith('local_') || order.id?.startsWith('pos_') || order.id?.startsWith('kiosk_')) {
            // Show "Draft" for draft status
            if (order.status === 'draft') {
                return 'Draft'
            }

            // Format as YY-MM-DDHHMMSS from timestamp or ID
            try {
                const timestamp = order.timestamp || order.created_at
                if (timestamp) {
                    const date = new Date(timestamp)
                    const yy = String(date.getFullYear()).slice(-2)
                    const mm = String(date.getMonth() + 1).padStart(2, '0')
                    const dd = String(date.getDate()).padStart(2, '0')
                    const hh = String(date.getHours()).padStart(2, '0')
                    const ii = String(date.getMinutes()).padStart(2, '0')
                    const ss = String(date.getSeconds()).padStart(2, '0')
                    return `${yy}-${mm}-${dd}${hh}${ii}${ss}`
                }
            } catch (err) {
                console.warn('Failed to format order timestamp:', err)
            }

            // Fallback: show simple order number if available
            return order.orderNumber || order.order_number || 'Draft'
        }

        // Fallback for any other orders
        return order.orderNumber || order.order_number || order.id || '...'
    }

    async function saveMessageTracking(uuid, eventName, payload, targetDevices) {
        if (!isDbReady.value) throw new Error('DB not ready')

        const acknowledgements = {}
        targetDevices.forEach(device => {
            acknowledgements[device] = {received: false, timestamp: null}
        })

        const now = new Date().toISOString()
        await executeQuery(
                `INSERT INTO message_tracking (uuid, timestamp, event_name, payload, target_devices, acknowledgements,
                                               retry_count, status, created_at)
                 VALUES (?, ?, ?, ?, ?, ?, 0, 'pending', ?)`,
                [uuid, Date.now(), eventName, JSON.stringify(payload), JSON.stringify(targetDevices), JSON.stringify(acknowledgements), now]
        )
    }

    async function updateMessageAcknowledgement(uuid, deviceId) {
        if (!isDbReady.value) throw new Error('DB not ready')

        const messages = await selectQuery(
                `SELECT acknowledgements, target_devices
                 FROM message_tracking
                 WHERE uuid = ?`,
                [uuid]
        )

        if (messages.length === 0) return

        const acknowledgements = JSON.parse(messages[0].acknowledgements)
        const targetDevices = JSON.parse(messages[0].target_devices)

        if (acknowledgements[deviceId]) {
            acknowledgements[deviceId].received = true
            acknowledgements[deviceId].timestamp = new Date().toISOString()
        }

        const allAcked = targetDevices.every(device => acknowledgements[device]?.received)
        const status = allAcked ? 'delivered' : 'pending'

        await executeQuery(
                `UPDATE message_tracking
                 SET acknowledgements = ?,
                     status           = ?
                 WHERE uuid = ?`,
                [JSON.stringify(acknowledgements), status, uuid]
        )
    }

    async function getUnacknowledgedMessages(maxRetries = 2) {
        if (!isDbReady.value) throw new Error('DB not ready')

        const messages = await selectQuery(
                `SELECT *
                 FROM message_tracking
                 WHERE status = 'pending'
                   AND retry_count < ?`,
                [maxRetries]
        )

        return messages.map(msg => ({
            uuid: msg.uuid,
            timestamp: msg.timestamp,
            eventName: msg.event_name,
            payload: JSON.parse(msg.payload),
            targetDevices: JSON.parse(msg.target_devices),
            acknowledgements: JSON.parse(msg.acknowledgements),
            retryCount: msg.retry_count
        }))
    }

    async function incrementMessageRetryCount(uuid) {
        if (!isDbReady.value) throw new Error('DB not ready')

        await executeQuery(
                `UPDATE message_tracking
                 SET retry_count = retry_count + 1
                 WHERE uuid = ?`,
                [uuid]
        )
    }

    async function markMessageAsFailed(uuid) {
        if (!isDbReady.value) throw new Error('DB not ready')

        await executeQuery(
                `UPDATE message_tracking
                 SET status = 'failed'
                 WHERE uuid = ?`,
                [uuid]
        )
    }

    async function hasProcessedMessage(uuid) {
        if (!isDbReady.value) throw new Error('DB not ready')

        const results = await selectQuery(
                `SELECT uuid
                 FROM processed_messages
                 WHERE uuid = ?`,
                [uuid]
        )

        return results.length > 0
    }

    async function markMessageAsProcessed(uuid, eventName, deviceId) {
        if (!isDbReady.value) throw new Error('DB not ready')

        const now = new Date().toISOString()
        await executeQuery(
                `INSERT OR IGNORE INTO processed_messages (uuid, processed_at, event_name, device_id)
                 VALUES (?, ?, ?, ?)`,
                [uuid, now, eventName, deviceId]
        )
    }

    async function cleanupOldMessages(daysOld = 7) {
        if (!isDbReady.value) throw new Error('DB not ready')

        const cutoffDate = new Date()
        cutoffDate.setDate(cutoffDate.getDate() - daysOld)
        const cutoffTimestamp = cutoffDate.getTime()

        await executeQuery(
                `DELETE
                 FROM message_tracking
                 WHERE timestamp < ?
                   AND status IN ('delivered', 'failed')`,
                [cutoffTimestamp]
        )

        const cutoffIso = cutoffDate.toISOString()
        await executeQuery(
                `DELETE
                 FROM processed_messages
                 WHERE processed_at < ?`,
                [cutoffIso]
        )
    }

    async function moveMessageToDLQ(uuid, failureReason = 'Max retries exceeded') {
        if (!isDbReady.value) throw new Error('DB not ready')

        const messages = await selectQuery(
                `SELECT *
                 FROM message_tracking
                 WHERE uuid = ?`,
                [uuid]
        )

        if (messages.length === 0) return

        const msg = messages[0]

        await executeQuery(
                `INSERT INTO failed_messages
                 (uuid, event_name, payload, target_devices, acknowledgements,
                  retry_count, first_attempt_at, failed_at, failure_reason)
                 VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                [
                    msg.uuid,
                    msg.event_name,
                    msg.payload,
                    msg.target_devices,
                    msg.acknowledgements,
                    msg.retry_count,
                    msg.created_at,
                    new Date().toISOString(),
                    failureReason
                ]
        )

        await executeQuery(
                `UPDATE message_tracking
                 SET status = 'failed'
                 WHERE uuid = ?`,
                [uuid]
        )
    }

    async function getFailedMessages() {
        if (!isDbReady.value) throw new Error('DB not ready')

        const messages = await selectQuery(
                `SELECT *
                 FROM failed_messages
                 WHERE manually_retried = 0
                 ORDER BY failed_at DESC`
        )

        return messages.map(msg => ({
            id: msg.id,
            uuid: msg.uuid,
            eventName: msg.event_name,
            payload: JSON.parse(msg.payload),
            targetDevices: JSON.parse(msg.target_devices),
            acknowledgements: JSON.parse(msg.acknowledgements),
            retryCount: msg.retry_count,
            firstAttemptAt: msg.first_attempt_at,
            failedAt: msg.failed_at,
            failureReason: msg.failure_reason
        }))
    }

    async function getFailedMessagesCount() {
        if (!isDbReady.value) throw new Error('DB not ready')

        const result = await selectQuery(
                `SELECT COUNT(*) as count
                 FROM failed_messages
                 WHERE manually_retried = 0`
        )

        return result[0]?.count || 0
    }

    async function retryFailedMessageFromDLQ(uuid) {
        if (!isDbReady.value) throw new Error('DB not ready')

        const failed = await selectQuery(
                `SELECT *
                 FROM failed_messages
                 WHERE uuid = ?
                   AND manually_retried = 0`,
                [uuid]
        )

        if (failed.length === 0) {
            throw new Error('Message not found or already retried')
        }

        const msg = failed[0]

        await executeQuery(
                `UPDATE failed_messages
                 SET manually_retried    = 1,
                     manually_retried_at = ?
                 WHERE uuid = ?`,
                [new Date().toISOString(), uuid]
        )

        return {
            eventName: msg.event_name,
            payload: JSON.parse(msg.payload),
            targetDevices: JSON.parse(msg.target_devices)
        }
    }

    async function clearAllFailedMessages() {
        if (!isDbReady.value) throw new Error('DB not ready')

        await executeQuery(`DELETE
                            FROM failed_messages`)
    }

    // --- Exported Interface ---
    // This is what other composables and components import when they call usePosDatabase()
    async function getInstanceId() {
        let instanceId = await getPosSetting('instance_id')

        if (!instanceId) {
            const {v4: uuidv4} = await import('uuid')
            instanceId = uuidv4()
            await savePosSetting('instance_id', instanceId)
            console.log('[usePosDatabase] Generated new instance ID:', instanceId)
        }

        return instanceId
    }

    async function saveAppLog(level, component, message, metadata = null) {
        if (!isDbReady.value) return
        try {
            const timestamp = new Date().toISOString()
            const normalizedMetadata = metadata === undefined ? null : metadata
            await executeQuery(
                `INSERT INTO app_logs (timestamp, level, component, message, metadata)
                 VALUES (?, ?, ?, ?, ?)`,
                [
                    timestamp,
                    String(level || 'INFO'),
                    String(component || 'unknown'),
                    String(message || ''),
                    normalizedMetadata ? JSON.stringify(normalizedMetadata) : null,
                ]
            )
        } catch (err) {
            console.warn('[usePosDatabase] Failed to save app log:', err)
        }
    }

    async function getAppLogs(options = {}) {
        if (!isDbReady.value) return []
        const {level, component, search, limit = 1000, offset = 0} = options
        let sql = `SELECT * FROM app_logs WHERE 1=1`
        const bindings = []

        if (level && level !== 'ALL') {
            sql += ` AND level = ?`
            bindings.push(level)
        }
        if (component) {
            sql += ` AND component = ?`
            bindings.push(component)
        }
        if (search) {
            sql += ` AND message LIKE ?`
            bindings.push(`%${search}%`)
        }

        sql += ` ORDER BY id DESC LIMIT ? OFFSET ?`
        bindings.push(Number(limit), Number(offset))

        return await selectQuery(sql, bindings)
    }

    async function deleteAppLogsBefore(dateISO) {
        if (!isDbReady.value) return {rowsAffected: 0}
        return await executeQuery(
            `DELETE FROM app_logs WHERE timestamp < ?`,
            [dateISO]
        )
    }

    async function deleteAllAppLogs() {
        if (!isDbReady.value) return {rowsAffected: 0}
        return await executeQuery(`DELETE FROM app_logs`, [])
    }

    async function getAppLogsCount() {
        if (!isDbReady.value) return 0
        const result = await selectQuery(`SELECT COUNT(*) as total FROM app_logs`, [])
        return Number(result[0]?.total || 0)
    }

    async function saveTerminalLog(data) {
        if (!isDbReady.value) return
        try {
            const createdAt = data.createdAt || new Date().toISOString()
            await executeQuery(
                `INSERT INTO terminal_transaction_logs
                 (provider, txn_type, txn_ref, station_id, status, response_code, response_text,
                  amount, duration_ms, xml_request, xml_response, parsed_response, error_message, order_id, created_at)
                 VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                [
                    data.provider || 'unknown',
                    data.txnType || null,
                    data.txnRef || null,
                    data.stationId || null,
                    data.status || null,
                    data.responseCode || null,
                    data.responseText || null,
                    data.amount != null ? Number(data.amount) : null,
                    data.durationMs != null ? Number(data.durationMs) : null,
                    data.xmlRequest || null,
                    data.xmlResponse || null,
                    data.parsedResponse ? (typeof data.parsedResponse === 'string' ? data.parsedResponse : JSON.stringify(data.parsedResponse)) : null,
                    data.errorMessage || null,
                    data.orderId != null ? Number(data.orderId) : null,
                    createdAt,
                ]
            )
        } catch (err) {
            console.warn('[usePosDatabase] Failed to save terminal log:', err)
        }
    }

    async function getTerminalLogs(options = {}) {
        if (!isDbReady.value) return []
        const {provider, txnType, status, txnRef, dateFrom, limit = 50, offset = 0} = options
        let sql = `SELECT * FROM terminal_transaction_logs WHERE 1=1`
        const bindings = []

        if (provider) {
            sql += ` AND provider = ?`
            bindings.push(provider)
        }
        if (txnType) {
            sql += ` AND txn_type = ?`
            bindings.push(txnType)
        }
        if (status) {
            sql += ` AND status = ?`
            bindings.push(status)
        }
        if (txnRef) {
            sql += ` AND txn_ref LIKE ?`
            bindings.push(`%${txnRef}%`)
        }
        if (dateFrom) {
            sql += ` AND created_at >= ?`
            bindings.push(dateFrom)
        }

        sql += ` ORDER BY id DESC LIMIT ? OFFSET ?`
        bindings.push(Number(limit), Number(offset))

        return await selectQuery(sql, bindings)
    }

    async function getTerminalLogsCount(options = {}) {
        if (!isDbReady.value) return 0
        const {provider, txnType, status} = options
        let sql = `SELECT COUNT(*) as total FROM terminal_transaction_logs WHERE 1=1`
        const bindings = []
        if (provider) { sql += ` AND provider = ?`; bindings.push(provider) }
        if (txnType) { sql += ` AND txn_type = ?`; bindings.push(txnType) }
        if (status) { sql += ` AND status = ?`; bindings.push(status) }
        const result = await selectQuery(sql, bindings)
        return Number(result[0]?.total || 0)
    }

    async function deleteTerminalLogsBefore(dateISO) {
        if (!isDbReady.value) return {rowsAffected: 0}
        return await executeQuery(
            `DELETE FROM terminal_transaction_logs WHERE created_at < ?`,
            [dateISO]
        )
    }

    async function deleteAllTerminalLogs() {
        if (!isDbReady.value) return {rowsAffected: 0}
        return await executeQuery(`DELETE FROM terminal_transaction_logs`, [])
    }

    // Readonly refs prevent external code from accidentally breaking our state
    return {
        isDbReady: readonly(isDbReady),
        isTauriMode: readonly(isTauriMode),
        isLoadingData: readonly(isLoadingData),
        initializeDatabase,
        syncBusinessData,
        getCachedData,
        saveCachedData,
        getDisplayOrderNumber,
        savePosSetting,
        getPosSetting,
        deletePosSetting,
        getConfiguredPrinters,
        saveConfiguredPrinter,
        deleteConfiguredPrinter,
        getScaleConfig,
        saveScaleConfig,
        saveLocalOrder,
        getLocalOrder,
        getAllLocalOrders,
        updateLocalOrderStatus,
        updateLocalOrderPaymentStatus,
        deleteLocalOrder,
        clearAllLocalOrders,
        getSavedOrder,
        printOrderObject,
        getNextOrderNumber,
        vacuumDatabase,
        syncOrderToBackend,
        getUnsyncedOrders,
        syncAllPendingOrders,
        pullOrdersFromBackend,
        executeQuery,
        saveMessageTracking,
        updateMessageAcknowledgement,
        getUnacknowledgedMessages,
        incrementMessageRetryCount,
        markMessageAsFailed,
        hasProcessedMessage,
        markMessageAsProcessed,
        cleanupOldMessages,
        moveMessageToDLQ,
        getFailedMessages,
        getFailedMessagesCount,
        retryFailedMessageFromDLQ,
        clearAllFailedMessages,
        getInstanceId,
        saveAppLog,
        getAppLogs,
        deleteAppLogsBefore,
        deleteAllAppLogs,
        getAppLogsCount,
        saveTerminalLog,
        getTerminalLogs,
        getTerminalLogsCount,
        deleteTerminalLogsBefore,
        deleteAllTerminalLogs,
    }
}

